import _ from "@/boot/lodash";
import { FraudStatusMap } from "./fraud";
import { CreditNoteStatus, InvoiceStatus } from "./enums/invoice";
import { TicketStatusCodes } from "./enums/tickets";
import { ImportStatusMap } from "./enums/imports";
import { ProvisionRequestStatusCodes as PRSC } from "./enums/contractProducts";
import i18n from "@/i18n";

export const InvoiceStatuses = {
  "0": {
    key: "unpaid",
    type: "is-warning"
  },
  "1": {
    key: "paid",
    type: "is-success"
  },
  [InvoiceStatus.DRAFT]: {
    key: "draft",
    type: "is-grey"
  },
  [CreditNoteStatus.ALLOCATED]: {
    key: "allocated",
    type: "is-grey"
  },
  [CreditNoteStatus.UNALLOCATED]: {
    key: "unallocated",
    type: "is-dark"
  },
  [InvoiceStatus.ADJUSTED]: {
    key: "unpaid",
    type: "is-warning"
  },
  [InvoiceStatus.UNPAID]: {
    key: "unpaid",
    type: "is-warning"
  },
  [InvoiceStatus.PAID]: {
    key: "paid",
    type: "is-success"
  },
  [InvoiceStatus.OVERDUE]: {
    key: "overdue",
    type: "is-danger"
  },
  [InvoiceStatus.REFUNDED]: {
    key: "refunded",
    type: "is-info"
  },
  [InvoiceStatus.CANCELLED]: {
    key: "cancelled",
    type: "is-grey"
  },
  [InvoiceStatus.REPLACED]: {
    key: "replaced",
    type: "is-info"
  }
};

export const OrderStatuses = InvoiceStatuses;

export const RequestStatuses = {
  [PRSC.PROVISION_REQUEST_DRAFT]: {
    label: i18n.t("_.draft"),
    key: "draft",
    type: "is-grey",
    icon: "drafting-compass",
    dateStamp: "updated_at"
  },
  [PRSC.PROVISION_REQUEST_SCHEDULED]: {
    label: i18n.t("_.scheduled"),
    key: "scheduled",
    type: "is-info",
    icon: "clock",
    dateStamp: "scheduled_for"
  },
  [PRSC.PROVISION_REQUEST_PENDING]: {
    label: i18n.t("_.pending"),
    key: "pending",
    type: "is-warning",
    icon: "hourglass-half",
    dateStamp: "updated_at"
  },
  [PRSC.PROVISION_REQUEST_DISPATCHED]: {
    label: i18n.t("_.dispatched"),
    key: "dispatched",
    type: "is-info",
    icon: "paper-plane",
    dateStamp: "dispatched_at"
  },
  [PRSC.PROVISION_REQUEST_SUCCESS]: {
    label: i18n.t("_.completed"),
    key: "success",
    type: "is-success",
    icon: "check-circle",
    dateStamp: "updated_at"
  },
  [PRSC.PROVISION_REQUEST_ERROR]: {
    label: i18n.t("_.errored"),
    key: "error",
    type: "is-danger",
    icon: "exclamation-circle",
    dateStamp: "updated_at"
  },
  [PRSC.PROVISION_REQUEST_CANCELLED]: {
    label: i18n.t("_.cancelled"),
    key: "cancelled",
    type: "is-grey",
    icon: "times-circle",
    dateStamp: "updated_at"
  }
};

export const ImportStatuses = _.mapValues(ImportStatusMap(), status =>
  _.pick(status, ["key", "type"])
);

export const Statuses = {
  oauthToken: {
    sandbox: {
      key: "sandbox",
      type: "is-caution"
    },
    revoked: {
      key: "revoked",
      type: "is-danger"
    }
  },
  verify: {
    "0": {
      key: "unverified",
      type: "is-danger"
    },
    "1": {
      key: "verified",
      type: "is-success"
    }
  },
  active: {
    "0": {
      key: "inactive",
      type: "is-grey"
    },
    "1": {
      key: "active",
      type: "is-success"
    }
  },
  address: {
    "1": {
      key: "home",
      type: "is-info"
    },
    "2": {
      key: "office",
      type: "is-info"
    },
    "3": {
      key: "holiday",
      type: "is-info"
    }
  },
  customFields: {
    "1": {
      key: "text_input",
      type: "is-info"
    },
    "2": {
      key: "password",
      type: "is-info"
    },
    "3": {
      key: "select",
      type: "is-info"
    },
    "4": {
      key: "checkbox",
      type: "is-info"
    },
    "5": {
      key: "textarea",
      type: "is-info"
    }
  },
  default: {
    "0": {
      key: "no",
      type: "is-grey"
    },
    "1": {
      key: "yes",
      type: "is-success"
    }
  },
  email: {
    "1": {
      key: "personal",
      type: "is-info"
    },
    "2": {
      key: "company",
      type: "is-info"
    }
  },
  enabled: {
    "0": {
      key: "disabled",
      type: "is-grey"
    },
    "1": {
      key: "enabled",
      type: "is-success"
    }
  },
  gateway: {
    "0": {
      key: "not-active",
      type: "is-grey"
    },
    "1": {
      key: "active",
      type: "is-success"
    }
  },
  paymentDetails: {
    "1": {
      key: "card_payment",
      type: "is-info"
    },
    "2": {
      key: "bank_transfer",
      type: "is-info"
    },
    "3": {
      key: "direct_debit",
      type: "is-info"
    },
    "4": {
      key: "sepa",
      type: "is-info"
    },
    "5": {
      key: "offline",
      type: "is-info"
    }
  },
  phone: {
    "1": {
      key: "mobile",
      type: "is-info"
    },
    "2": {
      key: "home",
      type: "is-info"
    },
    "3": {
      key: "office",
      type: "is-info"
    },
    "4": {
      key: "personal",
      type: "is-info"
    }
  },
  promotion: {
    "0": {
      key: "inactive",
      type: "is-grey"
    },
    "1": {
      key: "active",
      type: "is-success"
    }
  },
  imports: ImportStatuses,
  invoice: InvoiceStatuses,
  order: OrderStatuses,
  request: RequestStatuses,
  tickets: {
    [TicketStatusCodes.OPEN]: {
      key: "open",
      type: "is-success"
    },
    [TicketStatusCodes.CLOSED]: {
      key: "closed",
      type: "is-grey"
    },
    [TicketStatusCodes.IN_PROGESS]: {
      key: "in_progress",
      type: "is-info"
    },
    [TicketStatusCodes.CLIENT_REPLIED]: {
      key: "client_replied",
      type: "is-caution"
    },
    [TicketStatusCodes.AWAITING_RESPONSE]: {
      key: "awaiting_response",
      type: "is-dark"
    }
  },
  templates: {
    1: {
      label: i18n.t("_._default"),
      type: "is-info"
    }
  },
  bulkaction: {
    bulk_action_complete: {
      key: "complete",
      type: "is-success"
    },
    bulk_action_scheduled: {
      key: "scheduled",
      type: "is-warning"
    },
    bulk_action_in_progress: {
      key: "in_progress",
      type: "is-dark"
    },
    bulk_action_cancelled: {
      key: "cancelled",
      type: "is-dark"
    },
    bulk_action_incomplete: {
      key: "incomplete",
      type: "is-dark"
    },
    bulk_action_draft: {
      key: "draft",
      type: "is-dark"
    }
  },
  fraudDetectionLevel: {
    1: {
      key: "whitelist",
      type: "is-success"
    },
    2: {
      key: "greylist",
      type: "is-warning"
    },
    3: {
      key: "blacklist",
      type: "is-dark"
    }
  },
  fraudStatus: _.mapValues(FraudStatusMap(), status => ({
    label: status.label,
    type: status.type
  }))
};

const AddressTypes: any[] = [
  { key: 1, value: "home" },
  { key: 2, value: "office" },
  { key: 3, value: "holiday" }
];

const EmailTypes: any[] = [
  { key: 1, value: "personal" },
  { key: 2, value: "company" }
];

const VerifiedTypes: any[] = [
  { key: 0, value: "not_verified" },
  { key: 1, value: "verified" },
  { key: 2, value: "cant_verified" }
];

const PhoneTypes: any[] = [
  { key: 1, value: "mobile" },
  { key: 2, value: "home" },
  { key: 3, value: "office" },
  { key: 4, value: "personal" }
];

const PaymentDetailsTypes: any[] = [
  { key: 1, value: "card_payment" },
  { key: 2, value: "bank_transfer" },
  { key: 3, value: "direct_debit" },
  { key: 4, value: "sepa" },
  { key: 5, value: "offline" }
];

const TaxTypes: any[] = [
  { key: 0, value: "exclusive" },
  { key: 1, value: "inclusive" }
];

const CustomFields: any[] = [
  { key: 1, value: "text_input" },
  { key: 2, value: "password" },
  { key: 3, value: "select" },
  { key: 4, value: "checkbox" },
  { key: 5, value: "textarea" },
  { key: 6, value: "date" },
  { key: 7, value: "number" },
  { key: 8, value: "image" }
];

const ProductTypes: any[] = [
  { key: 1, value: "single_product" },
  { key: 2, value: "product_bundle" }
];

export {
  AddressTypes,
  CustomFields,
  EmailTypes,
  PaymentDetailsTypes,
  PhoneTypes,
  ProductTypes,
  TaxTypes,
  VerifiedTypes
};
